/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PhonebooksPage.tsx
 🗓️ Created: 2025-12-05
 🧩 Description:
    Phonebooks list page - Configurations for internal and external phonebooks
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Edit, Trash2, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModulePageContainer } from '../../components/layouts';
import { usePhonebooksList } from './hooks/usePhonebooksList';
import { deletePhonebook } from './api/phonebooksApi';
import { navigateToPhonebookDuplicate } from './phonebooksActions';
import type { Phonebook } from './types';

export default function PhonebooksPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    phonebook: Phonebook | null;
  }>({
    isOpen: false,
    phonebook: null,
  });

  const {
    phonebooks,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = usePhonebooksList();

  const handleEdit = useCallback(
    (phonebook: Phonebook) => {
      navigate(`/pbx/applications/phonebooks/${phonebook.uuid || phonebook.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (phonebook: Phonebook) => {
      navigateToPhonebookDuplicate(navigate, String(phonebook.uuid || phonebook.id));
    },
    [navigate]
  );

  const handleDelete = useCallback(
    (phonebook: Phonebook) => {
      setDeleteModal({ isOpen: true, phonebook });
    },
    []
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteModal.phonebook) return;

    try {
      await deletePhonebook(String(deleteModal.phonebook.uuid || deleteModal.phonebook.id));
      showSuccess(t('phonebooks.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, phonebook: null });
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.deleteError'));
    }
  }, [deleteModal.phonebook, refresh, showSuccess, showError, t]);

  const renderActions = useCallback(
    (phonebook: Phonebook) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(phonebook)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(phonebook)}
            className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDelete(phonebook)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDuplicate, handleDelete, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          title={t('phonebooks.title')}
          titleIcon={<BookOpen className="h-5 w-5 text-primary" />}
          data={phonebooks}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onSearch={handleSearch}
          onColumnToggle={handleColumnToggle}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          emptyMessage={t('phonebooks.noPhonebooksFound')}
          searchPlaceholder={t('phonebooks.searchPlaceholder')}
          renderActions={renderActions}
          addButtonText={t('common.add')}
          onAdd={() => navigate('/pbx/applications/phonebooks/new')}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll'),
          }}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, phonebook: null })}
        onConfirm={confirmDelete}
        title={t('phonebooks.deleteModal.title')}
        message={t('phonebooks.deleteModal.message', {
          description: deleteModal.phonebook?.description || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </ModulePageContainer>
  );
}
